import { batch } from 'react-redux';

import {
  setFirstMessageChatsBlocked,
  setFirstMessageFailed,
  setFirstMessageNeedCaptcha,
  setFirstMessageNeedConfirmPhone,
  setFirstMessagePending,
  setFirstMessageSent,
  setFirstMessageUserChatsDisabled,
  setFirstMessageValidationError,
} from './actions';
import { trackOpenNoLinksPopup, trackFirstMessageSent } from './tracking';
import { Messages } from './types';
import {
  fetchMessagesOffer,
  IMessagesOfferResponseError,
  TMessagesOfferResponse,
} from '../../repositories/chats/v1/messages/offer';
import {
  TSendFirstMessageToAgentResponse,
  fetchSendFirstMessageToAgent,
} from '../../repositories/chats/v1/send-first-message-to-agent';
import { hintQuestionsSelector } from '../../selectors/hintQuestionsSelector';
import { EAlert } from '../../types/alert';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { TThunkAction } from '../../types/redux/thunk';
import { showAlert } from '../alert';

interface ISendFirstMessageProps {
  /** Токен капчи(для проверки валидности, только desktop) **/
  captchaToken?: string;
  /** Идентификатор объявления **/
  offerId?: number;
  /** Текст сообщения **/
  text: string;
  /** Идентификатор агента **/
  agentId?: number;
}

/**
 * Отправка первого сообщения пользователем UI
 */
export const sendFirstMessage =
  (parameters: ISendFirstMessageProps): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { httpApi, logger }) => {
    const state = getState();
    const { openedChats, user } = state;

    const { ga } = openedChats['blob'];

    dispatch(setFirstMessagePending());

    let res;

    const { offerId, text, captchaToken, agentId } = parameters;

    try {
      if (offerId) {
        res = (await fetchMessagesOffer({
          httpApi,
          parameters: {
            offerId,
            text,
            captchaToken,
          },
        })) as TMessagesOfferResponse;
      }

      if (agentId) {
        res = (await fetchSendFirstMessageToAgent({
          httpApi,
          parameters: {
            userId: agentId,
            text,
            captchaToken,
          },
        })) as TSendFirstMessageToAgentResponse;
      }

      const { statusCode, response } = res || {};

      if (!response) {
        return;
      }

      if (statusCode === 200) {
        if (response.chatId) {
          const hintQuestions = hintQuestionsSelector(state);

          const { analyticsKey = null } = hintQuestions.find(({ messageText }) => messageText === text) || {};

          trackFirstMessageSent({
            label: ga?.label,
            gaProducts: ga?.products,
            response,
            userId: user.userId,
            analyticsKey,
          });

          dispatch(setFirstMessageSent({ chat: response }));

          return;
        }

        if (response.blocked) {
          batch(() => {
            dispatch(setFirstMessageChatsBlocked());
            dispatch(showAlert({ message: Messages[EFirstMessageStatus.ChatsBlocked] }));
          });

          return;
        }
      }

      if (response.message === 'needCaptcha') {
        dispatch(setFirstMessageNeedCaptcha());

        return;
      }

      if (response.message === 'needConfirmPhone') {
        batch(() => {
          dispatch(setFirstMessageNeedConfirmPhone());
          dispatch(showAlert({ message: Messages[EFirstMessageStatus.NeedConfirmPhone] }));
        });

        return;
      }

      if (response.message === 'userChatsDisabled') {
        batch(() => {
          dispatch(setFirstMessageUserChatsDisabled());
          dispatch(showAlert({ message: Messages[EFirstMessageStatus.ChatsDisabled] }));
        });

        return;
      }

      if (response.message === 'validationError' && 'errors' in response && response.errors && response.errors.length) {
        const isLinkError = response.errors.some(
          (error: { code: string }) => error.code === 'messageHasLinkToAnotherService',
        );

        if (isLinkError) {
          batch(() => {
            dispatch(setFirstMessageValidationError());
            dispatch(
              showAlert({ type: EAlert.MessageHasLinkToAnotherService, message: parameters.text, chatId: 'blob' }),
            );
          });
          trackOpenNoLinksPopup();

          return;
        }
      }

      batch(() => {
        dispatch(setFirstMessageFailed());
        dispatch(showAlert({ message: (response as IMessagesOfferResponseError).message }));
      });
    } catch (error) {
      logger.error(error, {
        domain: 'frontend-chats.actions.sendFirstMessage',
        description: 'Ошибка при отправке первого сообщения',
      });

      dispatch(showAlert());
    }
  };
